import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { type SampleNameTokenDefinitionV1 } from '@/model/SampleNameTemplate.model.ts';
import { _isNotBlank } from '@/littledash.ts';
import { type FC, useCallback } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import styles from './SampleNameTemplateBuilder.module.scss';
import type { MetadataGlossaryApiId } from '@/model/Metadata.model.ts';

export interface SequenceToken {
  localId: string;
  definition: SampleNameTokenDefinitionV1;
  customValue?: string;
  metadataGlossaryApiId?: MetadataGlossaryApiId;
  metadataGlossaryTitle?: string;
}

interface TemplateSequenceEditorProps {
  sequence: Array<SequenceToken>;
  onRemove: (localId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  readOnly?: boolean;
}

const chipLabel = (token: SequenceToken): string => {
  if (typeof token.customValue === 'string') {
    return `"${token.customValue}"`;
  }
  if (token.definition.type === 'separator') {
    return token.definition.value;
  }
  if (token.definition.value === 'sample_meta' && _isNotBlank(token.metadataGlossaryTitle)) {
    return token.metadataGlossaryTitle;
  }
  return token.definition.label;
};

/**
 * Displays the ordered token sequence as removable, draggable chips.
 */
export const TemplateSequenceEditor: FC<TemplateSequenceEditorProps> = ({
  sequence,
  onRemove,
  onReorder,
  readOnly = false,
}) => {
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || result.source.index === result.destination.index) {
        return;
      }
      onReorder(result.source.index, result.destination.index);
    },
    [onReorder]
  );

  if (sequence.length === 0) {
    return (
      <div className={styles.sequence}>
        <div className={styles.emptySequence}>Add components to build your template</div>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className={styles.sequence}>
        <div className={styles.sequenceRow}>
          {sequence.map((token) => {
            const isSeparator = token.definition.type === 'separator';
            return (
              <span
                key={token.localId}
                className={`${styles.chip} ${isSeparator ? styles.separatorChip : styles.tokenChip}`}
              >
                {chipLabel(token)}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={styles.sequence}>
        <Droppable droppableId="template-sequence" direction="horizontal">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className={styles.sequenceRow}>
              {sequence.map((token, index) => {
                const isSeparator = token.definition.type === 'separator';
                return (
                  <Draggable key={token.localId} draggableId={token.localId} index={index}>
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={dragProvided.draggableProps.style}
                        className={`${styles.chip} ${isSeparator ? styles.separatorChip : styles.tokenChip} ${snapshot.isDragging ? styles.chipDragging : ''}`}
                      >
                        <span
                          {...dragProvided.dragHandleProps}
                          className={styles.chipDragHandle}
                          aria-label="Drag to reorder"
                        >
                          ⠿
                        </span>
                        {chipLabel(token)}
                        <button
                          type="button"
                          className={styles.chipRemove}
                          onClick={() => onRemove(token.localId)}
                          aria-label={`Remove ${chipLabel(token)}`}
                        >
                          <RiCloseLine size={12} />
                        </button>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
};
